import { getFont } from '../state';
import { isNumeric } from '../utils/isNumeric';
import { scaleFont } from '../utils/scaleFont';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';
import { buildText, Text } from './Text';

const FUNCTION_SCALE = 0.7;
const BASE_OFFSET = 0.75;
const BASE_EXTRA = 5;
const TYPE_INSET = 5;

export class Function extends MathObject {
  showBase = false;

  type: Multi;
  base: Multi;
  value: Multi;

  constructor(line: Line, type: Multi, base: Multi, value: Multi) {
    super();

    this.t = 'function';
    this.gmmName = 'function';

    this.type = type;
    this.type.gmmName = 'base multi';
    this.base = base;
    this.base.gmmName = 'log base multi';
    this.value = value;
    this.value.gmmName = 'value multi';
    this.line = line;

    this.type.parent = this;
    this.base.parent = this;
    this.value.parent = this;
    this.setShowBase(true);

    scaleFont(this.base, (this.base.children[0] as Text).font, FUNCTION_SCALE);
  }

  getPaintedCharCount(): number {
    const a = this.type.getPaintedCharCount();
    const b = this.showBase ? this.base.getPaintedCharCount() : 0;
    const c = this.value.getPaintedCharCount();

    return a + b + c;
  }

  setEditable(b: boolean): void {
    this.type.setEditable(b);
    this.base.setEditable(b);
    this.value.setEditable(b);
  }

  setFont(f: string): void {
    this.type.setFont(f);
    this.value.setFont(f);
    scaleFont(this.base, f, FUNCTION_SCALE);
  }

  swapString(old: string, replacer: string): void {
    this.type.swapString(old, replacer);
    this.base.swapString(old, replacer);
    this.value.swapString(old, replacer);
  }

  hasDigitBase(): boolean {
    if (this.base.children.length > 1) return false;
    const t = this.base.children[0];

    if (t.gmmName !== 'text') return false;

    return isNumeric((t as Text).text);
  }

  setShowBase(showBase: boolean): void {
    this.showBase = showBase;
    this.children = [];
    this.children.push(this.type);
    if (showBase) this.children.push(this.base);
    this.children.push(this.value);
    this.buildSizeRecursive();
  }

  setAll(type: Multi, base: Multi, value: Multi): void {
    this.children = [];
    this.children.push(type);
    this.children.push(base);
    this.children.push(value);
    this.type = type;
    this.base = base;
    this.value = value;
    type.parent = this;
    base.parent = this;
    value.parent = this;
    scaleFont(
      this.base,
      this.base.findAnyChildFont() || getFont(),
      FUNCTION_SCALE
    );

    const typeText = (type.children[0] as Text).text;
    const baseText = (base.children[0] as Text).text;

    const supportsBase = typeText === 'log';

    this.setShowBase(
      supportsBase &&
        !((typeText === 'log' && baseText === '10') || base.isEmptyOrSpace())
    );
  }

  private ensureLeadingText(multi: Multi): Text {
    const current = multi.children[0];

    if (current instanceof Text && multi.children.length === 1) {
      return current;
    }

    const existingText = multi.children.find(child => child instanceof Text) as
      | Text
      | undefined;

    if (existingText) {
      multi.children = [existingText];
      existingText.parent = multi;

      return existingText;
    }

    multi.children = [];

    const replacement = buildText(multi.problemJS);
    const font = multi.findAnyChildFont();

    replacement.e = true;

    if (font) {
      replacement.setFont(font);
    }

    multi.add(replacement);

    return replacement;
  }

  setTypeAndBase(type: string, base: string, showBase: boolean): void {
    const text = this.ensureLeadingText(this.type);

    text.text = type;
    text.sizeMe();
    text.setCursorIndentChars(type.length);
    this.setShowBase(showBase);

    if (base) {
      const baseText = this.ensureLeadingText(this.base);

      baseText.text = base;
      baseText.sizeMe();
      baseText.setCursorIndentChars(base.length);
    }

    this.unsureWhichToFocus();
    this.buildSizeRecursive();
  }

  setLine(line: Line): void {
    this.line = line;
    this.type.setLine(line);
    this.base.setLine(line);
    this.value.setLine(line);
  }

  toXML(): string {
    return (
      '<tf><ft>' +
      this.type.toXML() +
      '</ft><fb>' +
      this.base.toXML() +
      '</fb><v>' +
      this.value.toXML() +
      '</v></tf>'
    );
  }

  grabFocus(d: Direction): void {
    if (d === 'left') {
      this.value.grabFocus(d);
    } else {
      this.type.grabFocus(d);
    }
  }

  unsureWhichToFocus(): void {
    const type = this.type.children[0] as MathObject;
    const base = this.base.children[0] as MathObject;

    if (
      (type.t === 'text' && (type as Text).text.length > 0) ||
      type.t != 'text'
    ) {
      if (
        !this.showBase ||
        (base.t === 'text' && (base as Text).text.length > 0) ||
        base.t != 'text'
      )
        this.value.grabFocus();
      else this.base.grabFocus();
    } else {
      this.type.grabFocus();
    }
  }

  buildSizeRecursive(): void {
    this.type.buildSizeRecursive();
    this.base.buildSizeRecursive();
    this.value.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    const w =
      this.type.viewportW +
      BASE_EXTRA +
      (this.showBase ? this.base.viewportW : 0) +
      this.value.viewportW;
    const h = Math.max(
      this.value.viewportH,
      this.showBase
        ? this.type.viewportH + this.base.viewportH + BASE_EXTRA
        : this.type.viewportH
    );

    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.value.getVertToBaseline();
  }
  // Woah, we had this function 'setPositionsOfChildren' twice in the megafile builder for Function,
  // so keeping the one I assume was getting discarded here as a comment in case debugging shows it was
  // somehow important.
  // setPositionsOfChildren(): void {
  //   this.type.viewportX = this.baseExtra;
  //   this.base.viewportX = this.type.viewportX + this.type.viewportW;
  //   this.value.viewportX = this.showBase
  //     ? this.base.viewportX + this.base.viewportW
  //     : this.type.viewportW + this.baseExtra;
  // }

  setPositionsOfChildren(): void {
    this.type.viewportX = TYPE_INSET;
    this.base.viewportX = this.type.viewportX + this.type.viewportW;
    this.value.viewportX = this.showBase
      ? this.base.viewportX + this.base.viewportW
      : this.type.viewportW + TYPE_INSET;

    this.type.viewportY = 0;
    this.value.viewportY = 0;

    const typeVertToBaseline = this.type.getVertToBaseline();
    const valueVertToBaseline = this.value.getVertToBaseline();

    // Position based off the larger baseline of the two
    const shift = Math.abs(
      this.value.getVertToBaseline() - this.type.getVertToBaseline()
    );

    if (typeVertToBaseline > valueVertToBaseline) this.value.viewportY = shift;
    else if (typeVertToBaseline < valueVertToBaseline)
      this.type.viewportY = shift;

    this.base.viewportY =
      this.type.viewportY + this.type.viewportH * BASE_OFFSET;
  }

  childSizeChanged(): void {
    this.buildSize();
    (this.parent as MathObject)?.childSizeChanged();
  }

  add(tile: MathObject, value: any): void {
    if (!value) {
      this.base.add(tile);
    } else {
      this.value.add(tile);
    }
  }
}

export function buildFunction(
  problemJS: MultiDependencies,
  line: Line,
  type?: Multi,
  base?: Multi,
  value?: Multi
): Function {
  return new Function(
    line,
    type || buildDefaultMulti(problemJS, line),
    base || buildDefaultMulti(problemJS, line),
    value || buildDefaultMulti(problemJS, line)
  );
}
